import { action, computed, observable, runInAction } from "mobx";
import moment from "moment";
import { FormStore } from "@modules/formx";
import { isEmpty } from "lodash";
import {
  ConsultantEventLog_GetEvents as getConsultantEvents,
  ConsultantProfileForQualityItem_GetConsultant as getConsultant,
  ConsultantProfileForQualityItemFragment,
  ConsultantQualityItemEditor_ClearStatus as clearStatus,
  ConsultantQualityItemEditor_Get as getData,
  ConsultantQualityItemEditor_SetApproved as setApproved,
  ConsultantQualityItemEditor_SetApprovedWithComment as setApprovedWithComment,
  ConsultantQualityItemEditor_SetRequestReceived as setRequestReceived,
  ConsultantQualityItemEditor_SetRequestSent as setRequestSent,
  ConsultantQualityItemEditor_SetBooked as setBooked,
  ConsultantQualityItemEditor_SetOrdered as setOrdered,
  ConsultantQualityItemEditor_SetConsultantResponsible as setConsultantResponsible,
  ConsultantQualityItemEditor_Upload as upload,
  ConsultantQualityItemEditorFragment,
  SendRequestIVO,
  ConsultantSearchCard_Get,
  ConsultantQualityReferenceVariant,
  ApproveCriminalRecordCheck
} from "@graphql";
import locale from "@bonliva-crm/locale";
import { store } from "@store";
import {
  FormData,
  nationalReferenceReferenceSchema,
  referenceSchema,
  schema,
  statusToMeta
} from "./form";
import { hasStatusParams } from "../quality-check/utils";
import { ivoNames } from "../quality-check/ivo-quality-check";
import { Snackbar } from "@bonliva-crm/ui";
import { getUtcDate } from "./utils";
import { Tenant, updatedResDate } from "@bonliva-crm/shared";

type Data = ConsultantQualityItemEditorFragment;

type ConsultantReference = {
  _id: string;
  Name?: string;
  Position?: string;
  variant: ConsultantQualityReferenceVariant;
};

export class ConsultantQualityItemEditorStore {
  @observable data: Data;
  @observable referenceData: any;
  @observable consultantReferences: Array<ConsultantReference> = [];
  @observable consultant: ConsultantProfileForQualityItemFragment = null;
  @observable error = null;
  @observable loading = true;
  @observable isDocumentRequired = false;
  @observable withDocument = false;
  @observable fileToUpload: File = null;

  @observable metadata: any = {};
  @observable metaCanSubmit = false;

  @computed get selectedScheme() {
    if (this.referenceId) {
      return this.isNationalReference
        ? nationalReferenceReferenceSchema
        : referenceSchema;
    }

    return schema;
  }

  @computed get referenceOpt() {
    return this.consultantReferences.map((r) => ({
      label: `${r.Name} - ${r.Position}`,
      value: r._id
    }));
  }

  @computed get name() {
    return this.referenceId ? this.referenceData?.name : this.data?.Name;
  }

  @computed get isCriminalRecord() {
    return ["Belastningsregister", "Belastningsregister Nationella"].includes(
      this.data?.Name
    );
  }

  form = new FormStore<FormData>({ schema: this.selectedScheme, initialDirty: true });

  constructor(
    private consultantId: string,
    private country: string,
    private itemName: string,
    private templateName: string,
    private onClose: () => void,
    private ivoGrundSetOn: Date,
    public referenceId: string,
    public isNationalReference: boolean,
    public snackbar: Snackbar,
    public tenant: Tenant
  ) {
    if (this.referenceId) {
      if (isNationalReference) {
        this.fetchNationalProcurementQualityReference();
      } else {
        this.fetchQualityReference();
      }
      this.form.validate();
    } else {
      this.fetch();
    }
    this.fetchConsultant();
    (window as any).consultantQualityItemEditorStore = this;
  }

  get availableStatusNames() {
    let result = [];

    if (this.data?.AvailableStatuses) {
      const filtered = this.data.AvailableStatuses.filter((x) =>
        ivoNames.includes(this.itemName) ? x.Name !== "Ordered" : true
      ).map((x) => x.Name);

      result.push(...filtered);
    }

    if (this.data?.Name.includes("Belastnings")) {
      result = result.filter((x) => x !== "RequestSent");
    }

    return result;
  }

  get selectedStatusIndex() {
    if (this.referenceId) {
      return this.form.values.selectedStatus;
    }
    const result = this.form.values.selectedStatus
      ? this.data.AvailableStatuses.findIndex(
          (x) => x.Name === this.form.values.selectedStatus
        )
      : 0;

    return result;
  }

  get statusOptions() {
    return this.data?.AvailableStatuses[this.selectedStatusIndex];
  }

  get canSubmit() {
    return (this.form.canSubmit || this.metaCanSubmit) && !this.isDocumentRequired;
  }

  get canCancel() {
    return this.referenceId
      ? Boolean(this.referenceData?.status)
      : Boolean(this.data?.Status);
  }

  get isCommentRequired() {
    return this.statusOptions.IsCommentRequired;
  }

  get withComment() {
    return this.statusOptions.WithComment;
  }

  get commentLabel() {
    return this.data.Name === "HSAID/SITHS" ? locale.CardNumber : locale.Comment;
  }

  requestUpload = () =>
    store()
      .upload.requestFileUpload({
        multiple: false
      })
      .then((files) => {
        this.fileToUpload = files[0];
      });

  handleUpload = async () => {
    if (!this.fileToUpload || !this.form.values.selectedStatus) {
      return;
    }

    this.loading = true;

    return upload
      .func({
        consultantId: this.consultantId,
        documentType: this.data.DocumentType,
        statusName: this.form.values.selectedStatus,
        file: this.fileToUpload
      })
      .then(() => this.fetch())
      .catch((err) => {
        this.snackbar.showError(`Uploading failed\nReason: ${err}`);
      })
      .finally(() => {
        this.loading = false;
      });
  };

  closeUpload = () => (this.fileToUpload = null);

  submitApprovedWithComment = () =>
    this.submit(
      setApprovedWithComment.func(
        {
          consultantId: this.consultantId,
          country: this.country,
          qualityCheckName: this.itemName,
          setOn: this.form.values.date,
          comment: this.metadata.comment
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName
          ]
        }
      )
    );

  submitApproved = () => {
    if (this.isCriminalRecord) {
      this.submit(
        ApproveCriminalRecordCheck.func(
          {
            consultantId: this.consultantId,
            qualityCheckName: this.itemName,
            qualityTemplate: this.templateName,
            params: {
              setOn: this.form.values.date
            }
          },
          {
            refetchQueries: [
              getConsultantEvents.OperationName,
              ConsultantSearchCard_Get.OperationName
            ]
          }
        )
      );
    } else {
      this.submit(
        setApproved.func(
          {
            consultantId: this.consultantId,
            country: this.country,
            qualityCheckName: this.itemName,
            setOn: this.form.values.date,
            comment: this.metadata.comment
          },
          {
            refetchQueries: [
              getConsultantEvents.OperationName,
              ConsultantSearchCard_Get.OperationName
            ]
          }
        )
      );
    }
  };

  submitReference = () => {};

  submitNationalProcurementReference = () => {};

  submitRequestSent = () =>
    this.submit(
      setRequestSent.func(
        {
          consultantId: this.consultantId,
          country: this.country,
          qualityCheckName: this.itemName,
          setOn: this.form.values.date,
          params:
            hasStatusParams(this.data.Status) &&
            this.data.Status.params.criminalRecordType
              ? { criminalRecordType: this.data.Status.params.criminalRecordType }
              : null
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName
          ]
        }
      )
    );

  @computed get sendIVODate() {
    if (this.itemName === "IVO-Uppdatering" && this.ivoGrundSetOn) {
      return moment(this.ivoGrundSetOn).format("YYYY-MM-DD");
    } else {
      const resDate = this.consultant?.expertise?.professions
        .map((x) => x.certifiedFrom)
        .reduce(updatedResDate, null);

      return resDate;
    }
  }

  @computed get sendRequestIVOBody() {
    if (!this.consultant) {
      return "";
    }

    return `
      Hej,

      Kan ni vänligen kontrollera nedanstående persons ärendehistorik (pågående/avslutade ärenden)?

      ${this.consultant.Name}
      ${this.consultant?.SocialSecurityNo?.split("-").join("")}
      Legitimerad sedan: ${this.sendIVODate || "Ej angett"}

      Vi vill att ni söker på både namn och personnummer för att säkerställa att all information/historik följer med.

      Tack på förhand!
  `;
  }

  @computed get sendIVOUpdateBody() {
    if (!this.consultant) {
      return "";
    }

    return `
      Hej,

      Kan ni vänligen kontrollera nedanstående persons ärendehistorik (pågående/avslutade ärenden)?

      ${this.consultant.Name}
      ${this.consultant.SocialSecurityNo}
      Senaste kontroll: ${this.sendIVODate}

      Vi vill att ni söker på både namn och personnummer för att säkerställa att all information/historik följer med. Vi vill att ni söker från 1e jan det år då senaste kontroll gjordes.

      Tack på förhand!
    `;
  }

  @computed get ivoBody() {
    let body = "";
    if (this.itemName === "IVO-Uppdatering") {
      body = this.sendIVOUpdateBody;
    }
    if (this.itemName === "IVO-Grund") {
      body = this.sendRequestIVOBody;
    }

    return body.replace(/\n/gi, "<br>");
  }

  submitSendRequestIVO = () => {
    const ivoTo = {
      email: "kontrollavlegitimeradpersonal@ivo.se",
      name: "IVO",
      avatarPic: "kontrollavlegitimeradpersonal"
    };

    // If user is in IT team - allow to send from own email (for testing)
    const testUserOrNone = store().user.inTeam("IT")
      ? [store().user.myProfile.Email]
      : [];

    store().emailEditor.requestEmailEditor(
      {
        from: this.tenant === "wcc" ? "kvk@weconnectcare.se" : "",
        subject: "Kontroll IVO, Bonliva",
        body: this.ivoBody,
        to: [ivoTo],
        attachments: []
      },
      async (emailData) => {
        if (!emailData) {
          // user has cancelled the editor
          return;
        }
        try {
          const { data } = await SendRequestIVO.func(
            {
              body: emailData.body,
              subject: emailData.subject,
              consultantId: this.consultantId,
              country: this.country,
              qualityCheckName: this.itemName,
              setOn: this.form.values.date,
              from: emailData.from
            },
            {
              refetchQueries: [
                getConsultantEvents.OperationName,
                ConsultantSearchCard_Get.OperationName
              ]
            }
          );

          if (data) {
            store().emailEditor.close();
            this.snackbar.showSuccess(locale["Email sent successfully"]);
          }
        } catch (err) {
          this.snackbar.showError(`Cannot send email\nReason: ${err}`);
        }
      },
      {
        withToEditor: false,
        fromOptions: this.tenant === "wcc" ? testUserOrNone : ["kvk@bonliva.se"]
      }
    );
  };

  submitRequestReceivedNotProcessed = () =>
    this.submit(
      setRequestReceived.func(
        {
          consultantId: this.consultantId,
          country: this.country,
          qualityCheckName: this.itemName,
          setOn: this.form.values.date,
          comment: this.metadata.comment
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName
          ]
        }
      )
    );

  submitOrdered = () => {
    this.submit(
      setOrdered.func(
        {
          consultantId: this.consultantId,
          country: this.country,
          qualityCheckName: this.itemName,
          setOn: this.form.values.date
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName
          ]
        }
      )
    );
  };

  submitBooked = () => {
    this.submit(
      setBooked.func(
        {
          consultantId: this.consultantId,
          country: this.country,
          qualityCheckName: this.itemName,
          setOn: this.form.values.date
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName
          ]
        }
      )
    );
  };

  submitConsultantResponsible = () => {
    this.submit(
      setConsultantResponsible.func(
        {
          consultantId: this.consultantId,
          country: this.country,
          qualityCheckName: this.itemName,
          setOn: this.form.values.date
        },
        {
          refetchQueries: [
            getConsultantEvents.OperationName,
            ConsultantSearchCard_Get.OperationName
          ]
        }
      )
    );
  };

  clearStatus = () => {
    if (this.referenceId && !this.isNationalReference) {
      // Removed
    } else if (this.referenceId && this.isNationalReference) {
      // Removed
    } else {
      this.submit(
        clearStatus.func(
          {
            consultantId: this.consultantId,
            country: this.country,
            qualityCheckName: this.itemName
          },
          {
            refetchQueries: [
              getConsultantEvents.OperationName,
              ConsultantSearchCard_Get.OperationName
            ]
          }
        )
      );
    }
  };

  @action setStatus = async (statusName: string) => {
    if (!statusName) {
      return;
    }

    if (!this.referenceId) {
      const status = this.data?.AvailableStatuses.find((x) => x.Name === statusName);

      if (!status) {
        throw new Error(`Cannot find status ${status.Name}`);
      }

      this.form.setValue("selectedStatus", status.Name);

      if (status.Name === "RequestSentIVO") {
        // Fire Click right away for RequestSentIVO
        await this.submitSendRequestIVO();
      }

      if (status.Name === "RequestSent") {
        this.form.setValue("date", new Date());
      }
    } else {
      this.form.setValue("selectedStatus", statusName);
    }
  };

  checkStatus = (statusName: string) => {
    if (!statusName) {
      return {
        disabled: false
      };
    }

    const status = this.data?.AvailableStatuses.find((x) => x.Name === statusName);

    if (statusName === "Approved") {
      const allowedToApprove = ["RequestSent", "Approved"];

      if (this.isCriminalRecord && !allowedToApprove.includes(this.data.Status?.Name)) {
        return {
          disabled: true
        };
      }

      if (this.isCriminalRecord && !store().user.can("consultant-criminal-record:approve:all")) {
        return {
          disabled: true
        };
      }

      if (ivoNames.includes(this.itemName)) {
        return { disabled: !this.fileToUpload };
      }
    }

    return {
      disabled:
        status && status.WithDocument && status.IsDocumentRequired
          ? !this.fileToUpload
          : false
    };
  };

  setStatusMetaData(metadata: any) {
    this.metadata = metadata;
  }

  setStatusCanSubmit(canSubmit: boolean) {
    this.metaCanSubmit = canSubmit;
  }

  private fetchQualityReference = () => {
    this.loading = false;
    return null;
  };

  private fetchNationalProcurementQualityReference = () => {
    this.loading = false;
    return null;
  };

  private getConsultantReferences(consultant: any) {
    const consulantReferences: ConsultantReference[] = [];

    if (consultant.References?.length) {
      consulantReferences.push(...consultant.References);
    }

    if (consultant.EvaluationsByCustomer?.length) {
      consulantReferences.push(...consultant.EvaluationsByCustomer);
    }

    if (consultant.EvaluationsToThreeMonthsByCustomer?.length) {
      consulantReferences.push(...consultant.EvaluationsToThreeMonthsByCustomer);
    }

    return consulantReferences.filter((x) => x.Name);
  }

  private fetch = (refetch = false) => {
    this.loading = true;

    return getData
      .func(
        {
          consultantId: this.consultantId,
          country: this.country,
          itemName: this.itemName
        },
        { fetchPolicy: "network-only" }
      )
      .then(({ data }) => {
        runInAction(() => {
          const prevStatus = this.form.values.selectedStatus;
          this.setData(data.getConsultantQualityItem);
          this.setStatus(prevStatus);
        });
      })
      .catch((err) => {
        this.snackbar.showError(`Cannot fetch quality item`);
        console.error(err);
        this.error = err;
      })
      .finally(() => {
        this.loading = false;
      });
  };

  @action private setData = (data: ConsultantQualityItemEditorFragment) => {
    this.data = data;
    this.withDocument = this.data.AvailableStatuses.some((x) => x.WithDocument);

    // if there is a status - date should be treat as initial value
    // otherwise is just value (form is dirty)
    if (this.data.Status) {
      this.form.reset({
        date: moment(
          this.form.dirty && this.form.values.selectedStatus === this.data.Status.Name
            ? this.form.values.date
            : getUtcDate(this.data.Status.SetOn)
        ).toDate(),
        selectedStatus: this.data.Status.Name
      });

      this.metadata = statusToMeta(this.data.Status);
    } else {
      this.form.setValue("date", this.form.dirty ? this.form.values.date : getUtcDate());

      if (!isEmpty(this.data.AvailableStatuses)) {
        this.setStatus(this.data.AvailableStatuses[0].Name);
      }
    }
  };

  private submit = (promise: Promise<any>) => {
    this.loading = true;

    promise
      .then(() => {
        this.snackbar.showSuccess(locale.Saved);
        this.onClose();
      })
      .catch((err) => {
        this.snackbar.showError(`Cannot save.\nReason: ${err}`);
      })
      .finally(() => {
        this.loading = false;
      });
  };

  private fetchConsultant() {
    getConsultant
      .func({
        id: this.consultantId
      })
      .then(({ data }) => {
        this.consultant = data.consultantById;
      })
      .catch((e) => console.log(e));
  }
}
